import { CHECKOUT_MAX_QUANTITY_LIMIT } from "@pufimi/shared";
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Browser-local Cart (ADR-0054 §2). A list of `{ productId, quantity }` kept in
// localStorage — the Products it references are server rows owned via the anonymous
// session; the cart itself is only this local selection (no server `carts` table, no
// GC). It is now the SOLE browser-local funnel memory: the returning-buyer path is the
// cart itself, persisted here — the single-pointer Resume store (ADR-0051) was retired
// once the cart superseded it (ADR-0056). Grouping-by-project is a derivation over the
// server cart quote (see features/cart/grouping), not stored here.

export const CART_STORAGE_KEY = "pufimi:cart:v1";

// How long an untouched cart survives in the browser. Deliberately the SAME 7 days as the
// server's anonymous retention (`ANON_GC_WINDOW_MS`, ADR-0054 §7) and the anonymous
// session cookie: past that window the Products a cart points at are reaped and the
// session that owned them is gone, so every line would come back unavailable anyway. This
// is the pre-flight half of that self-heal — it stops a months-old cart from lighting up
// the nav badge with products that no longer exist before any request is made. A cart the
// buyer touched more recently is kept and left to the server quote, which prunes per line.
export const CART_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// One cart line as the browser holds it — a server productId + a quantity. The full
// display (thumbnail, variant, price) comes from the server cart quote; the local
// store keeps ONLY what the client is allowed to own (never a price).
export interface CartLine {
  productId: string;
  quantity: number;
}

// Clamp a quantity to [1, absolute limit]. The lower, operator-editable
// `checkout_max_quantity` (#240) is enforced server-side; this is the client sanity
// bound so a stepper can never drive a nonsense value into the request.
function clampQuantity(quantity: number): number {
  return Math.min(CHECKOUT_MAX_QUANTITY_LIMIT, Math.max(1, Math.trunc(quantity)));
}

// A cart's lines plus their freshness stamp, which is derived from the lines rather than tracked
// beside them: an EMPTY cart has no freshness to keep, so its stamp is always null. That makes the
// persisted shape identical however the cart got empty — `clear()` on payment success, or the
// buyer removing the last line — instead of leaving a "last changed" time on a cart with nothing
// in it to expire. Every mutation goes through here, so the two can never drift apart.
function stamped(items: CartLine[]): { items: CartLine[]; savedAt: number | null } {
  return { items, savedAt: items.length > 0 ? Date.now() : null };
}

interface CartState {
  items: CartLine[];
  // When the cart was last changed, epoch ms — the freshness stamp `CART_TTL_MS` reads on
  // rehydrate. `null` for an empty cart and for a payload persisted before this field
  // existed (which is then kept, and left to the server quote to prune).
  savedAt: number | null;
  // Idempotent "add to cart" (ADR-0054 §2): the preview's "Zamów" and the resume-to-
  // checkout ensure a project's Product is present. Present → no-op (does NOT inflate
  // the quantity on a re-add / a resume); absent → added at quantity 1.
  ensureProduct: (productId: string) => void;
  // Set a line's quantity from the cart stepper, clamped to [1, absolute limit].
  setQuantity: (productId: string, quantity: number) => void;
  // Remove a line entirely (the cart's per-line remove).
  removeProduct: (productId: string) => void;
  // Drop the whole cart (payment success hands off to the Order view).
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      savedAt: null,
      ensureProduct: (productId) =>
        set((s) =>
          s.items.some((line) => line.productId === productId)
            ? s
            : stamped([...s.items, { productId, quantity: 1 }]),
        ),
      setQuantity: (productId, quantity) =>
        set((s) =>
          stamped(
            s.items.map((line) =>
              line.productId === productId ? { ...line, quantity: clampQuantity(quantity) } : line,
            ),
          ),
        ),
      removeProduct: (productId) =>
        set((s) => stamped(s.items.filter((line) => line.productId !== productId))),
      clear: () => set(stamped([])),
    }),
    {
      name: CART_STORAGE_KEY,
      // Persist the lines + their freshness stamp; the actions are recreated on load.
      partialize: (state) => ({ items: state.items, savedAt: state.savedAt }),
      // Drop a cart that outlived the retention window on the way IN (`merge` runs before
      // anything renders, so the badge never shows a count for reaped products). An
      // undated payload — written before `savedAt` existed — is kept; the server quote
      // prunes whatever of it is actually gone.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<Pick<CartState, "items" | "savedAt">>;
        const savedAt = saved.savedAt ?? null;
        const stale = savedAt !== null && Date.now() - savedAt > CART_TTL_MS;
        return {
          ...current,
          items: stale ? [] : (saved.items ?? []),
          savedAt: stale ? null : savedAt,
        };
      },
    },
  ),
);

// The nav badge count: the total number of units across all lines (a 2× + 1× cart
// reads "3"). Selected as a scalar so the badge re-renders only when it changes.
export function cartCount(items: CartLine[]): number {
  return items.reduce((sum, line) => sum + line.quantity, 0);
}
